import { memo, useEffect, useState, useCallback } from 'react';
import { Clock, Image, Video, Sparkles, Upload, RefreshCw, Trash2, AlertTriangle, Music, Wand2, Zap, Film } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { sendAssetToTimeline } from '../../utils/sendAssetToTimeline';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_MAP = {
  enhance_photo: { icon: Sparkles, label: 'Mejorada', assetType: 'image' },
  create_video: { icon: Video, label: 'Video', assetType: 'video' },
  nano_banana: { icon: Wand2, label: 'Nano Banana', assetType: 'image' },
  background_removal: { icon: Image, label: 'Sin Fondo', assetType: 'image' },
  style_transfer: { icon: Wand2, label: 'Estilizada', assetType: 'image' },
  upscale: { icon: Zap, label: 'Escalada', assetType: 'image' },
  lyria2: { icon: Music, label: 'Lyria 2', assetType: 'audio' },
  lyria3_pro: { icon: Music, label: 'Lyria 3', assetType: 'audio' },
  frame_extractor: { icon: Film, label: 'Frame', assetType: 'image' },
  qwen3_tts: { icon: Music, label: 'TTS', assetType: 'audio' },
  video_with_sound: { icon: Video, label: 'Video+Audio', assetType: 'video' },
  fabric_avatar: { icon: Video, label: 'Avatar', assetType: 'video' },
  dream_actor: { icon: Video, label: 'DreamActor', assetType: 'video' },
  kling_avatar: { icon: Video, label: 'Kling', assetType: 'video' },
};

const FILTER_TABS = [
  { key: 'all', label: 'Todos' },
  { key: 'image', label: 'Mejoradas' },
  { key: 'video', label: 'Videos' },
  { key: 'audio', label: 'Música' },
];

const AssetHistory = memo(() => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [todayOnly, setTodayOnly] = useState(false);
  const assets = useWorkflowStore((state) => state.assets);
  const loadAssets = useWorkflowStore((state) => state.loadAssets);
  const addNode = useWorkflowStore((state) => state.addNode);

  const loadAssetsFromBackend = useCallback(async () => {
    setLoading(true);
    // Retry for auth timing (interceptor may not be ready on first mount)
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await axios.get(`${API}/executions`, { params: { limit: 50 } });
        const executions = res.data.executions || [];

        const allAssets = executions
          .filter(exec => exec.status === 'completed' && exec.result_url)
          .map(exec => {
            const toolInfo = TOOL_MAP[exec.result_type] || { icon: Image, label: exec.result_type, assetType: 'image' };
            return {
              id: exec.id,
              assetId: exec.result_asset_id || null,
              type: toolInfo.assetType,
              tool: exec.result_type,
              filename: `${toolInfo.label}_${(exec.id || '').slice(-6)}`,
              url: exec.result_url,
              prompt: exec.input_params?.prompt,
              createdAt: exec.created_at,
            };
          });

        loadAssets(allAssets);
        setTotalCount(allAssets.length);
        setLoading(false);
        return; // success
      } catch (error) {
        if (error.response?.status === 401 && attempt < 2) {
          await new Promise(r => setTimeout(r, 800));
          continue; // retry
        }
        console.error('Error loading assets:', error.message);
      }
    }
    setLoading(false);
  }, [loadAssets]);

  useEffect(() => {
    loadAssetsFromBackend();
  }, [loadAssetsFromBackend]);

  // E6c.5 · el aviso "Tenés N generación(es) más en la galería · Ver" abre
  // la galería mostrando sólo las de hoy, que es donde están.
  useEffect(() => {
    const onOpen = (e) => {
      setTodayOnly(e.detail?.scope === 'today');
      loadAssetsFromBackend();
    };
    window.addEventListener('gallery:open', onOpen);
    return () => window.removeEventListener('gallery:open', onOpen);
  }, [loadAssetsFromBackend]);

  const handleClearAllHistory = async () => {
    setClearingHistory(true);
    try {
      await axios.delete(`${API}/executions/clear`);
      loadAssets([]);
      setTotalCount(0);
      setShowClearModal(false);
    } catch (error) {
      console.error('Error clearing history:', error.message);
    } finally {
      setClearingHistory(false);
    }
  };

  const handleUseAsset = async (asset) => {
    const position = {
      x: 100 + Math.random() * 100,
      y: 100 + Math.random() * 100,
    };

    let nodeType = 'uploadImage';
    let outputType = 'image';
    if (asset.type === 'video') { nodeType = 'uploadVideo'; outputType = 'video'; }
    else if (asset.type === 'audio') { nodeType = 'uploadAudio'; outputType = 'audio'; }

    addNode(nodeType, position, {
      preview: asset.url,
      fileName: asset.filename,
      hasOutput: true,
      outputUrl: asset.url,
      outputType,
      fromAsset: true,
      assetId: asset.assetId || null,
    });
  };

  const handleSendToTimeline = async (asset, e) => {
    e?.stopPropagation();
    if (asset.type === 'audio') {
      await sendAssetToTimeline(asset.assetId, {
        fallbackUrl: asset.url, fallbackKind: 'audio', label: asset.filename,
      });
      return;
    }
    await sendAssetToTimeline(asset.assetId, {
      fallbackUrl: asset.url,
      fallbackKind: asset.type,
      label: asset.filename,
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const byTab = activeTab === 'all'
    ? assets
    : assets.filter((asset) => {
        const toolInfo = TOOL_MAP[asset.tool];
        return toolInfo?.assetType === activeTab;
      });

  // Filtro "hoy": lo prende el aviso del nodo, se apaga con su chip.
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const filteredAssets = todayOnly
    ? byTab.filter((a) => a.createdAt && new Date(a.createdAt) >= startOfToday)
    : byTab;

  return (
    <div className="asset-history" data-testid="asset-history">
      <div className="asset-history-header">
        <h3>
          <Clock size={16} />
          Assets
          {totalCount > 0 && (
            <span className="asset-count-badge">{totalCount}</span>
          )}
        </h3>
        <div className="asset-header-actions">
          <button
            className="refresh-btn"
            onClick={loadAssetsFromBackend}
            disabled={loading}
            title="Refrescar"
            data-testid="assets-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            className="clear-history-btn"
            onClick={() => setShowClearModal(true)}
            title="Limpiar históricos"
            disabled={totalCount === 0}
            data-testid="assets-clear-btn"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="asset-tabs">
        {FILTER_TABS.map(tab => (
          <button
            key={tab.key}
            className={`asset-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`asset-tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
        {todayOnly && (
          <button
            className="asset-tab today-chip active"
            onClick={() => setTodayOnly(false)}
            title="Quitar el filtro de hoy"
            data-testid="asset-tab-today"
          >
            Hoy ✕
          </button>
        )}
      </div>

      <div className="asset-list">
        {loading ? (
          <div className="asset-loading">
            <RefreshCw size={24} className="animate-spin" />
            <span>Cargando assets...</span>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="asset-empty">
            <Image size={32} />
            <span>No hay assets</span>
            <p>Los resultados de tus procesos aparecerán aquí</p>
          </div>
        ) : (
          filteredAssets.slice(0, 30).map((asset) => {
            const toolInfo = TOOL_MAP[asset.tool] || { icon: Image, label: asset.tool };
            const ToolIcon = toolInfo.icon;
            return (
              <div
                key={asset.id}
                className="asset-item"
                onClick={() => handleUseAsset(asset)}
                data-testid={`asset-${asset.id}`}
              >
                <div className="asset-thumbnail">
                  {asset.type === 'video' ? (
                    <video src={asset.url} className="asset-image" muted preload="none" onError={() => setBrokenImages(prev => new Set([...prev, asset.id]))} />
                  ) : asset.type === 'audio' ? (
                    <div className="asset-audio-placeholder">
                      <Music size={24} />
                    </div>
                  ) : brokenImages.has(asset.id) ? (
                    <div className="asset-placeholder">
                      <Image size={20} />
                    </div>
                  ) : (
                    <img
                      src={asset.url}
                      alt={asset.filename}
                      className="asset-image"
                      onError={() => setBrokenImages(prev => new Set([...prev, asset.id]))}
                    />
                  )}
                  <div className="asset-type-badge">
                    <ToolIcon size={10} />
                    <span>{toolInfo.label}</span>
                  </div>
                </div>
                <div className="asset-info">
                  <span className="asset-filename">{asset.filename}</span>
                  <span className="asset-date">{formatDate(asset.createdAt)}</span>
                </div>
                <div className="asset-actions">
                  <button className="asset-use-btn" data-testid={`asset-use-${asset.id}`}>
                    Usar
                  </button>
                  <button
                    className="asset-timeline-btn"
                    onClick={(e) => handleSendToTimeline(asset, e)}
                    title="Enviar al Timeline"
                    data-testid={`asset-timeline-${asset.id}`}
                  >
                    <Film size={11} /> Timeline
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Clear History Modal */}
      {showClearModal && (
        <div className="clear-history-modal-overlay" onClick={() => setShowClearModal(false)}>
          <div className="clear-history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="clear-modal-header">
              <AlertTriangle size={24} className="warning-icon" />
              <h4>Limpiar Históricos</h4>
            </div>
            <p>¿Estás seguro de que deseas eliminar todos los históricos?</p>
            <p className="clear-modal-warning">Esta acción no se puede deshacer.</p>
            <div className="clear-modal-actions">
              <button
                className="clear-modal-cancel"
                onClick={() => setShowClearModal(false)}
                disabled={clearingHistory}
              >
                Cancelar
              </button>
              <button
                className="clear-modal-confirm"
                onClick={handleClearAllHistory}
                disabled={clearingHistory}
              >
                {clearingHistory ? (
                  <><RefreshCw size={14} className="animate-spin" /> Limpiando...</>
                ) : (
                  <><Trash2 size={14} /> Limpiar Todo</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

AssetHistory.displayName = 'AssetHistory';

export default AssetHistory;
